import { MiniGameBase } from './base.js';

/**
 * "Timeline Challenge" sequential mini-game
 * Player arranges events in chronological order
 * Same mechanic as Put in Order but displays dates/years
 */
export class TimelineGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.currentOrder = this.shuffleItems();
        this.completed = false;
        this.draggedElement = null;
    }

    /**
     * Shuffle items for initial display
     */
    shuffleItems() {
        const items = [...this.data.items];
        return this.shuffleArray(items);
    }

    /**
     * Render the timeline UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Put these events in chronological order:'}</div>
                <div class="ordering-hint">Arrange from earliest to latest</div>
                <div class="ordering-container">
                    <div class="ordering-list">
                        ${this.currentOrder.map((item, idx) => `
                            <div class="ordering-item timeline-item" draggable="true" data-index="${idx}">
                                <div class="event-label">${item.event}</div>
                                ${item.year ? `<div class="event-year">${item.year}</div>` : ''}
                            </div>
                        `).join('')}
                    </div>
                </div>
                <div class="button-row">
                    <button class="submit-button">Check Timeline</button>
                </div>
                <div class="feedback-message" style="display: none;"></div>
            </div>
        `;

        // Add skip button before submit button
        const buttonRow = container.querySelector('.button-row');
        const submitBtn = buttonRow.querySelector('.submit-button');
        const skipBtn = this.createSkipButton();
        buttonRow.insertBefore(skipBtn, submitBtn);

        this.attachEventListeners();
    }

    /**
     * Attach drag-and-drop and button listeners
     */
    attachEventListeners() {
        const items = this.container.querySelectorAll('.ordering-item');
        const submitButton = this.container.querySelector('.submit-button');

        // Drag and drop (desktop)
        items.forEach((item, idx) => {
            item.addEventListener('dragstart', (e) => this.handleDragStart(e, idx));
            item.addEventListener('dragover', (e) => this.handleDragOver(e));
            item.addEventListener('drop', (e) => this.handleDrop(e, idx));
            item.addEventListener('dragend', (e) => this.handleDragEnd(e));

            // Touch support (mobile)
            this.setupTouchDrag(
                item,
                () => this.playSelectSound(),
                (dragIdx, dropIdx) => this.handleTouchDrop(dragIdx, dropIdx),
                '.ordering-item',
                idx
            );

            // Attach hint if this event matches a word in category
            const eventName = this.currentOrder[idx]?.event;
            if (eventName) {
                this.attachHintIfExists(item, eventName);
            }
        });

        // Submit button
        submitButton.addEventListener('click', () => this.handleSubmit());
    }

    /**
     * Handle touch drop
     */
    handleTouchDrop(dragIndex, dropIndex) {
        if (dragIndex === dropIndex) return;

        // Reorder items
        const draggedItem = this.currentOrder[dragIndex];
        this.currentOrder.splice(dragIndex, 1);
        this.currentOrder.splice(dropIndex, 0, draggedItem);

        // Re-render
        this.render(this.container);
    }

    /**
     * Handle drag start
     */
    handleDragStart(e, index) {
        this.playSelectSound();
        this.draggedElement = index;
        e.target.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
    }

    /**
     * Handle drag over
     */
    handleDragOver(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        e.currentTarget.classList.add('drag-over');
    }

    /**
     * Handle drop
     */
    handleDrop(e, dropIndex) {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        if (this.draggedElement === null || this.draggedElement === dropIndex) {
            return;
        }

        // Swap items in currentOrder
        const draggedItem = this.currentOrder[this.draggedElement];
        this.currentOrder.splice(this.draggedElement, 1);
        this.currentOrder.splice(dropIndex, 0, draggedItem);

        // Re-render
        this.render(this.container);
    }

    /**
     * Handle drag end
     */
    handleDragEnd(e) {
        e.target.classList.remove('dragging');

        // Remove drag-over class from all items
        const items = this.container.querySelectorAll('.ordering-item');
        items.forEach(item => item.classList.remove('drag-over'));

        this.draggedElement = null;
    }

    /**
     * Handle submit button click
     */
    handleSubmit() {
        const isCorrect = this.checkAnswer();
        const feedback = this.container.querySelector('.feedback-message');
        const submitButton = this.container.querySelector('.submit-button');

        feedback.style.display = 'block';

        if (isCorrect) {
            this.playMatchSound();
            feedback.className = 'feedback-message success';
            feedback.textContent = 'Perfect Timeline!';
            this.completed = true;
            submitButton.disabled = true;

            // Disable dragging
            const items = this.container.querySelectorAll('.ordering-item');
            items.forEach(item => {
                item.draggable = false;
                item.style.cursor = 'default';
            });

            setTimeout(() => this.showCelebration(), 1000);
        } else {
            this.playErrorSound();
            feedback.className = 'feedback-message error';
            feedback.textContent = 'Not quite right. Check the chronological order!';

            // Shake animation
            const list = this.container.querySelector('.ordering-list');
            list.classList.add('error');
            setTimeout(() => {
                list.classList.remove('error');
                feedback.style.display = 'none';
            }, 2000);
        }

        return { action: isCorrect ? 'correct' : 'incorrect' };
    }

    /**
     * Check if current order matches correct chronological order
     */
    checkAnswer() {
        // Generate correct order from items sorted by 'order' field
        const correctOrder = [...this.data.items].sort((a, b) => {
            // Sort by order field if present, otherwise maintain original order
            const orderA = a.order !== undefined ? a.order : 0;
            const orderB = b.order !== undefined ? b.order : 0;
            return orderA - orderB;
        });

        if (this.currentOrder.length !== correctOrder.length) {
            return false;
        }

        // Compare by event name (or could compare by year if provided)
        return this.currentOrder.every((item, idx) => {
            const correctItem = correctOrder[idx];
            // Support both object format {event, year} and string format
            const currentEvent = typeof item === 'object' ? item.event : item;
            const correctEvent = typeof correctItem === 'object' ? correctItem.event : correctItem;
            return currentEvent === correctEvent;
        });
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completed;
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('reorder' in selection) {
            this.currentOrder = selection.reorder;
            this.render(this.container);
            return { action: 'reordered' };
        }
        return { action: 'none' };
    }
}
